import { PropsWithChildren, useEffect } from "react";

import { Color, useLamp } from "../Lamp";
import {
    CheckoutSuccessSubStateKind,
    OutOfOrderSubStateKind,
    RootState,
    RootStateKind,
    ScanSubStateKind,
    useAnyState,
    WelcomeSubStateKind
} from "./state/StateProvider";

const lampForState = (
    state: RootState
): { color: Color } | { blink: Color; color: Color } | undefined => {
    switch (state.kind) {
        case RootStateKind.OutOfOrder:
            if (
                state.subState.kind ===
                OutOfOrderSubStateKind.OperatorVerification
            ) {
                return { color: "Yellow" };
            }
            return { color: "Red" };
        case RootStateKind.Welcome:
            switch (state.subState.kind) {
                case WelcomeSubStateKind.Resetting:
                    return {
                        color: "Green"
                    };
                case WelcomeSubStateKind.OperatorVerification:
                    return { color: "Yellow" };
                default:
                    return { color: "Green" };
            }
        case RootStateKind.Scan:
            switch (state.subState.kind) {
                case ScanSubStateKind.AgeVerification:
                    if (state.subState.status === "underage") {
                        return {
                            blink: "Orange",
                            color: "Blue"
                        };
                    }
                    return { color: "Orange" };
                case ScanSubStateKind.CartCheck:
                    return { color: "Yellow" };
                case ScanSubStateKind.CheckoutError:
                    return { blink: "Red", color: "Blue" };
                case ScanSubStateKind.Loading:
                    return undefined;
                default:
                    return { color: "Blue" };
            }
        case RootStateKind.CheckoutSuccess:
            switch (state.subState.kind) {
                case CheckoutSuccessSubStateKind.Idle:
                    return { blink: "Green", color: "Green" };
                default:
                    return { color: "Green" };
            }
        case RootStateKind.Operator:
            return { color: "Yellow" };
        case RootStateKind.Flappy:
            return { color: "Blue" };
        case RootStateKind.Error:
            return { color: "Red" };
        default:
            return state satisfies never;
    }
};

class LampCommandBuffer {
    private pending?: () => Promise<void>;
    private draining = false;

    next(command: () => Promise<void>) {
        this.pending = command;
        if (!this.draining) void this.drain();
    }

    private async drain() {
        this.draining = true;
        while (this.pending !== undefined) {
            const command = this.pending;
            this.pending = undefined;
            await command();
        }
        this.draining = false;
    }
}

const lampCommandBuffer = new LampCommandBuffer();

export const LampController = ({ children }: PropsWithChildren) => {
    const { state } = useAnyState();
    const { setColor, setBlink } = useLamp();

    useEffect(() => {
        const command = lampForState(state);
        if (!command) return;
        lampCommandBuffer.next(async () => {
            if ("blink" in command) {
                await setBlink(command.blink);
                await setColor(command.color);
            } else {
                await setColor(command.color);
            }
        });
    }, [state]);

    return <>{children}</>;
};
